<template>
    <div class="container-fluid default-dashboard">
        <div class="card">
            <div class="card-header">
                <div class="d-flex justify-content-between align-items-center">
                    <h5>Clinic List</h5>
                    <button class="btn btn-primary" @click="openModal()">
                        <i class="fa fa-plus"></i> Add Clinic
                    </button>
                </div>
            </div>
            <div class="card-body">
                <DataTable
                    :headers="headers"
                    :items="clinics"
                    :loading="loading"
                    :total="total"
                    :params="params"
                    @update:params="updateParams"
                >
                    <template #item.actions="{ item }">
                        <button class="btn btn-sm btn-warning me-2" @click="editClinic(item as any)">
                            <i class="fa fa-edit"></i>
                        </button>
                        <button class="btn btn-sm btn-danger" @click="deleteClinic(item as any)">
                            <i class="fa fa-trash"></i>
                        </button>
                    </template>
                </DataTable>
            </div>
        </div>
    </div>

    <div class="modal fade bd-example-modal-lg" id="clinicModal" tabindex="-1" ref="modalRef" aria-labelledby="clinicModalLabel" aria-hidden="true">
        <div class="modal-dialog modal-lg" role="document">
            <div class="modal-content">
                <h3 class="modal-header justify-content-center border-0">{{ modalTitle }}</h3>
                    <div class="modal-body">
                        <div class="row">
                            <div class="col-md-6 mb-2">
                                <label class="form-label col-form-label">Name</label>
                                <input v-model="form.name" type="text" class="form-control" required>
                            </div>
                            <div class="col-md-6 mb-2">
                                <label class="form-label col-form-label">Description</label>
                                <input v-model="form.description" type="text" class="form-control" required>
                            </div>
                        </div>
                    </div>
                    <div class="modal-footer">
                        <button class="btn btn-danger" type="button" @click="closeModal">Cancel</button>
                        <button class="btn btn-primary" type="submit" @click="saveClinic">Save</button>
                    </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { ref, reactive, onMounted } from 'vue'
import { toast } from 'vue3-toastify'
import 'vue3-toastify/dist/index.css'
import DataTable from '@/components/table/Datatable.vue'
import { Modal } from 'bootstrap'

const clinics = ref<any[]>([])
const loading = ref(false)
const total = ref(0)
const modalTitle = ref('')
const params = reactive<DataTableParams>({
    page: 1,
    size: 10,
    search: ''
})

const headers = [
    { text: 'Name', value: 'name' },
    { text: 'Description', value: 'description' },
    { text: 'Actions', value: 'actions' }
]

const initForm = {
    ID: null,
    name: '',
    description: '',
}

const form = reactive({
    ...initForm
})

const dataClinics: any[] = []
import * as setting from "@/api/services/SettingRequest";

const fetchClinics = async () => {
    loading.value = true
    try {
        const resp = await setting.postRequest("/clinic/list", {...params});
        dataClinics.splice(0, dataClinics.length);
        dataClinics.push(...resp.data);

        let filteredClinics = [...dataClinics]
        if (params.search) {
            const searchLower = params.search.toLowerCase()
            filteredClinics = dataClinics.filter(clinic =>
                clinic.code.toLowerCase().includes(searchLower) ||
                clinic.name.toLowerCase().includes(searchLower)
            )
        }

        const start = (params.page - 1) * params.size
        const end = start + params.size
        clinics.value = filteredClinics.slice(start, end)
        total.value = filteredClinics.length

    } catch (error) {
        toast.error('Failed to fetch clinics')
    }
    loading.value = false
}

const updateParams = (newParams: Partial<DataTableParams>) => {
    Object.assign(params, newParams)
    fetchClinics()
}

const modalRef = ref<HTMLElement | null>(null)
let bsModal: any | null = null

const viewModalRef = ref<HTMLElement | null>(null)
let bsViewModal: any | null = null
const selectedClinic = ref<any | null>(null)

onMounted(() => {
    if (modalRef.value) {
        bsModal = new Modal(modalRef.value)
    }
    if (viewModalRef.value) {
        bsViewModal = new Modal(viewModalRef.value)
    }
    fetchClinics()
})

const closeModal = () => {
    if (bsModal) {
        bsModal.hide()
    }
}

const editClinic = (clinic: any) => {
    openModal(clinic);
}

const saveClinic = async () => {
    loading.value = true
    try {
        if (form.ID) {
            await setting.postRequest("/clinic/update", form).then((resp) => {
                if (resp.status === 200) {
                    toast.success('Clinic updated successfully');
                    closeModal();
                    fetchClinics();
                } else {
                    toast.error('Failed to update clinic');
                }
            }).catch((error) => {
                toast.error(error.message || 'Failed to update clinic');
            })
        } else {
            await setting.postRequest("/clinic/create", form).then((resp) => {
                if (resp.status === 200) {
                    toast.success('Clinic created successfully');
                    closeModal();
                    fetchClinics();
                } else {
                    toast.error('Failed to create clinic');
                }
            }).catch((error) => {
                toast.error(error.message || 'Failed to create clinic');
            })
        }
    } catch (error: any) {
        toast.error(error.message || 'Failed to save clinic');
    } finally {
        loading.value = false
    }
}

const deleteClinic = async (clinic: any) => {
    if (!confirm('Are you sure you want to delete this clinic?')) return

    loading.value = true
    try {
        await setting.postRequest("/clinic/delete", {ID: clinic.ID}).then((resp) => {
            if (resp.status === 200) {
                toast.success('Clinic deleted successfully');
                closeModal();
                fetchClinics();
            } else {
                toast.error('Failed to delete clinic');
            }
        })

    } catch (error: any) {
        toast.error(error.message || 'Failed to delete clinic');
    } finally {
        loading.value = false
    }
}

const openModal = (clinic: any | null = null) => {
    if (clinic) {
        modalTitle.value = 'Edit Clinic'
        Object.assign(form, clinic);
    } else {
        modalTitle.value = 'Create Clinic'
        Object.assign(form, {...initForm})
    }
    if (bsModal) {
        bsModal.show()
    }
}
</script>